import React, { useEffect, useRef, useMemo } from "react";
import ApexCharts from "apexcharts";
import { THEME_COLORS } from "../../../../../../src/color";
import { useSelector } from "react-redux";
const ProgressChart = ({
  title,
  mainLabel,
  categories,
  mainValue,
  totalValue,
  primaryColor = "#E6EEEC",
  secondaryColor = "#458276",
  primaryLegendLabel,
  secondaryLegendLabel,
  icon,
  legendPosition = "bottom",
  footertitle,
  className = "",
  lacvalue,
  status_card = false,
  barHeights,
  barwidth,
}) => {
  const { user } = useSelector((state) => state.auth);
  const isPermissionCompleted = user?.isCompleted;
  //console.log(isPermissionCompleted, "isPermissionCompleted");

  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const percentage = useMemo(() => {
    if (totalValue === 0) return 0;
    return ((mainValue / totalValue) * 100).toFixed(0);
  }, [mainValue, totalValue]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        sparkline: { enabled: true },
        height: 80,
      },
      series:
        totalValue === 0
          ? [
              { name: secondaryLegendLabel, data: [0] },
              { name: primaryLegendLabel, data: [100] },
            ]
          : [
              { name: secondaryLegendLabel, data: [mainValue] },
              {
                name: primaryLegendLabel,
                data: [Math.max(0, totalValue - mainValue)],
              },
            ],
      colors: [primaryColor, secondaryColor],
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: barHeights || "40%",
        },
      },
      xaxis: {
        categories: [mainLabel],
        labels: { show: false },
        axisBorder: { show: false },
      },
      yaxis: {
        labels: { show: false },
        axisBorder: { show: false },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, { seriesIndex }) => {
          return seriesIndex === 0
            ? percentage === "0"
              ? "0%"
              : `${percentage}%`
            : "";
        },
        style: { fontSize: "12px", fontWeight: "bold", colors: ["#FFF"] },
      },
      legend: { show: false },
      tooltip: { enabled: false },
      grid: { show: false },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
    }),
    [
      mainValue,
      totalValue,
      percentage,
      primaryColor,
      secondaryColor,
      mainLabel,
      primaryLegendLabel,
      secondaryLegendLabel,
    ],
  );

  useEffect(() => {
    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = new ApexCharts(chartRef.current, chartOptions);
        chartInstance.current.render();
      } else {
        chartInstance.current.updateOptions(chartOptions);
      }
    }
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartOptions]);

  return (
    <div className={`w-full h-full flex flex-col ${className}`}>
      <div className="flex items-center text-[#458276] gap-[4px] flex-shrink-0">
        {icon}
        <h2
          className="font-calibri  font-semibold text-base md:text-lg"
          style={{ color: THEME_COLORS.green }}
        >
          {title}
        </h2>
      </div>

      <div
        className={`flex flex-col md:flex-row md:justify-between md:items-center gap-2 md:gap-4 flex-grow ${
          !isPermissionCompleted ? "blur-sm opacity-60 pointer-events-none" : ""
        }`}
      >
        {/* Chart Container */}
        <div className="w-full md:w-3/5 flex flex-col justify-center pl-4">
          <div
            ref={chartRef}
            className="w-full"
            style={{ height: "80px" }}
          ></div>
          {lacvalue && (
            <div className="text-xs text-gray-500 mt-1 font-medium">
              {lacvalue}
            </div>
          )}
          {footertitle && <h3 className="text-sm mt-1">{footertitle}</h3>}
        </div>

        {/* Legend */}
        <div
          className={`flex ${
            legendPosition === "right"
              ? "flex-col items-start"
              : "flex-row flex-wrap items-center"
          } gap-4`}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: primaryColor }}
            ></div>
            <span className="text-xs md:text-sm">
              {secondaryLegendLabel}:{" "}
              {status_card ? totalValue : `${percentage}%`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: secondaryColor }}
            ></div>
            <span className="text-xs md:text-sm">
              {primaryLegendLabel}: {status_card ? mainValue : totalValue}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressChart;
